/**
 * ErrorBoundary — a per-section React error boundary. A throw inside one section is
 * caught, reported to the optional onError hook, and replaced by a fallback; sibling
 * sections are unaffected. This is why the Builder Engine never crashes. See
 * ERROR_HANDLING.md.
 */
import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface Props {
  readonly fallback: (error: Error) => ReactNode
  readonly onError?: (error: Error, info: ErrorInfo) => void
  readonly children: ReactNode
}

interface State {
  readonly error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info)
  }

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback(this.state.error)
    }
    return this.props.children
  }
}
